<template>
  <v-container
    fluid
    class="test-checklists-page font-weight-medium pa-0"
  >
    <!-- Access Denied for non-dev users -->
    <v-row
      v-if="!isUserInternalDev"
      class="justify-center align-center"
      style="min-height: 400px;"
    >
      <v-col
        cols="12"
        sm="8"
        md="6"
      >
        <v-card class="text-center pa-6">
          <font-awesome-icon
            :icon="['fas', 'lock']"
            size="3x"
            class="mb-4 grey--text"
          />
          <v-card-title class="justify-center headline">
            Access Restricted
          </v-card-title>
          <v-card-text class="body-1">
            QA Checklists are only available to internal developers.
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content for authorized users -->
    <v-row
      v-else
      class="no-gutters"
    >
      <!-- Sidebar with checklist navigation -->
      <v-col
        class="checklist-sidebar d-none d-md-block"
        cols="auto"
        style="width: 240px; min-width: 240px;"
      >
        <div class="sidebar-sticky">
          <div :class="`sidebar-header caption font-weight-bold text-uppercase px-4 pt-4 pb-2 ${nearblackTextClass}`">
            QA Checklists
          </div>
          <v-list
            dense
            nav
            class="py-0"
          >
            <v-list-item
              v-for="checklist in availableChecklists"
              :key="checklist.id"
              :class="{ 'primary--text font-weight-bold': selectedChecklist === checklist.id }"
              class="cursor-pointer"
              @click="selectChecklist(checklist.id)"
            >
              <v-list-item-icon class="mr-2 align-center">
                <font-awesome-icon
                  :icon="checklist.icon"
                  size="sm"
                />
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="caption">
                  {{ checklist.title }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="checklist.coverage">
                <v-chip
                  x-small
                  :color="getCoverageColor(checklist.coverage)"
                  text-color="white"
                >
                  {{ checklist.coverage }}%
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>
      </v-col>

      <!-- Main Content Area -->
      <v-col>
        <v-card class="py-6 px-8 elevation-0">
          <!-- Header -->
          <v-row class="align-center mb-4">
            <v-col>
              <v-card-title class="primary--text headline font-weight-medium pa-0">
                {{ currentChecklist.title }}
              </v-card-title>
              <v-card-subtitle class="pa-0 mt-0 font-weight-medium">
                {{ currentChecklist.description }}
              </v-card-subtitle>
            </v-col>
            <v-col cols="auto">
              <v-btn
                class="text-none mx-4 elevation-0"
                @click="copyToClipboard"
              >
                <font-awesome-icon
                  :icon="['fas', 'copy']"
                  :class="{ 'mr-2': $vuetify.breakpoint.smAndUp }"
                />
                <span class="d-none d-sm-inline">Copy Markdown</span>
              </v-btn>

              <v-btn
                v-if="currentChecklist.previewUrl"
                class="text-none elevation-0"
                :href="currentChecklist.previewUrl"
                target="_blank"
              >
                <font-awesome-icon
                  :icon="['fas', 'external-link-alt']"
                  :class="{ 'mr-2': $vuetify.breakpoint.smAndUp }"
                />
                <span class="d-none d-sm-inline">Preview Site</span>
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="mb-4" />

          <!-- Test Summary -->
          <v-row v-if="currentChecklist.summary">
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                outlined
                class="text-center pa-3"
              >
                <div :class="`caption font-weight-medium ${nearblackTextClass}`">
                  Total Items
                </div>
                <div class="headline font-weight-bold">
                  {{ currentChecklist.summary.total }}
                </div>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                outlined
                class="text-center pa-3"
              >
                <div :class="`caption font-weight-medium ${nearblackTextClass}`">
                  Passed
                </div>
                <div class="headline font-weight-bold success--text">
                  {{ currentChecklist.summary.passed }}
                </div>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                outlined
                class="text-center pa-3"
              >
                <div :class="`caption font-weight-medium ${nearblackTextClass}`">
                  Failed
                </div>
                <div class="headline font-weight-bold error--text">
                  {{ currentChecklist.summary.failed }}
                </div>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                outlined
                class="text-center pa-3"
              >
                <div :class="`caption font-weight-medium ${nearblackTextClass}`">
                  Skipped
                </div>
                <div class="headline font-weight-bold warning--text">
                  {{ currentChecklist.summary.skipped }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- QA Coverage Info -->
          <v-row class="mt-0">
            <v-col>
              <v-card
                outlined
                class="pa-3"
              >
                <v-card-text :class="`caption font-weight-medium ${nearblackTextClass}`">
                  QA coverage is based on the percentage of checklist items that have been verified as passed in the most recent test run. A higher coverage percentage indicates more comprehensive testing and greater confidence in the stability of the release.
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Validation Snapshot -->
          <v-row class="mb-4 mt-0">
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                outlined
                class="text-center pa-3"
              >
                <div :class="`caption font-weight-medium ${nearblackTextClass}`">
                  Release Window
                </div>
                <div class="body-2 font-weight-bold">
                  Since v4.72.3
                </div>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                outlined
                class="text-center pa-3"
              >
                <div :class="`caption font-weight-medium ${nearblackTextClass}`">
                  Covered Suites
                </div>
                <div class="body-2 font-weight-bold">
                  {{ validationSnapshot.coveredSuites }} / {{ validationSnapshot.totalSuites }}
                </div>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                outlined
                class="text-center pa-3"
              >
                <div :class="`caption font-weight-medium ${nearblackTextClass}`">
                  Verified Items
                </div>
                <div class="body-2 font-weight-bold success--text">
                  {{ validationSnapshot.passed }} / {{ validationSnapshot.total }}
                </div>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                outlined
                class="text-center pa-3"
              >
                <div :class="`caption font-weight-medium ${nearblackTextClass}`">
                  Coverage
                </div>
                <div class="body-2 font-weight-bold">
                  {{ validationSnapshot.coverage }} <span :class="coveragePercentTextClass">%</span>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="mb-4" />

          <!-- Section Title: Checklist Details -->
          <v-row>
            <v-col>
              <h2 :class="`subtitle-1 font-weight-bold mb-3 ${nearblackTextClass}`">
                Checklist Details
              </h2>
            </v-col>
          </v-row>

          <!-- Checklist Sections -->
          <div
            v-for="section in currentChecklist.sections"
            :key="section.id"
            class="checklist-section mb-6"
          >
            <h3 :class="`subtitle-1 font-weight-bold mb-2 ${nearblackTextClass}`">
              {{ section.title }}
              <v-chip
                x-small
                class="ml-2"
                :color="getSectionColor(section)"
              >
                {{ getSectionProgress(section) }}
              </v-chip>
            </h3>

            <!-- Subsections -->
            <div
              v-for="subsection in section.subsections"
              :key="subsection.id"
              class="ml-4 mb-4"
            >
              <h4 class="body-2 font-weight-bold text--darken-2 mb-2">
                {{ subsection.title }}
              </h4>

              <!-- Checklist Items -->
              <div
                v-for="item in subsection.items"
                :key="item.id"
                class="checklist-item d-flex align-start mb-1"
              >
                <v-icon
                  small
                  class="mr-2 mt-1"
                  :color="getItemColor(item)"
                >
                  {{ getItemIcon(item) }}
                </v-icon>
                <div class="flex-grow-1">
                  <span :class="nearblackTextClass">
                    {{ item.text }}
                  </span>
                  <span
                    v-if="item.note"
                    class="caption ml-2"
                    :class="getNoteClass(item.note)"
                  >
                    — {{ item.note }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Remaining Items Table -->
          <div
            v-if="currentChecklist.remainingItems && currentChecklist.remainingItems.length > 0"
            class="mt-6"
          >
            <h3 :class="`subtitle-1 font-weight-bold mb-3 ${nearblackTextClass}`">
              Remaining Untested Items
            </h3>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Item</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in currentChecklist.remainingItems"
                  :key="item.id"
                >
                  <td class="caption font-weight-medium">
                    <span class="pa-2 pl-0">{{ item.category }}</span>
                  </td>
                  <td class="caption font-weight-medium">
                    <div class="pa-2 pl-0">
                      {{ item.item }}
                    </div>
                  </td>
                  <td class="caption font-weight-medium">
                    <div class="pa-2 pl-0">
                      {{ item.reason }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- Notes -->
          <div
            v-if="currentChecklist.notes && currentChecklist.notes.length > 0"
            class="mt-6"
          >
            <h3 :class="`subtitle-1 font-weight-bold mb-3 ${nearblackTextClass}`">
              Notes
            </h3>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Issue</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="note in currentChecklist.notes"
                  :key="getNoteKey(note)"
                >
                  <td class="caption font-weight-medium">
                    <div class="pa-2 pl-0 font-weight-bold">
                      {{ getNoteIssue(note) }}
                    </div>
                  </td>
                  <td class="caption font-weight-medium">
                    <div class="pa-2 pl-0">
                      {{ getNoteDescription(note) }}
                    </div>
                  </td>
                  <td>
                    <v-chip
                      x-small
                      :color="getNoteStatusColor(note.status)"
                      text-color="white"
                    >
                      {{ note.status }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- Footer -->
          <v-divider class="my-4" />
          <div class="caption grey--text">
            <strong>Last Tested:</strong> {{ currentChecklist.lastTested || 'Not yet tested' }}
            <span
              v-if="currentChecklist.testedBy"
              class="ml-4"
            >
              <strong>Tested By:</strong> {{ currentChecklist.testedBy }}
            </span>
            <span
              v-if="currentChecklist.environment"
              class="ml-4"
            >
              <strong>Environment:</strong> {{ currentChecklist.environment }}
            </span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar for copy confirmation -->
    <v-snackbar
      v-model="showSnackbar"
      :timeout="2000"
      color="success"
    >
      Checklist copied to clipboard!
    </v-snackbar>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import themeClasses from '@/mixins/themeClasses'
import pacingChecklist from '@/docs/data/checklists/pacing.json'
import projectsChecklist from '@/docs/data/checklists/projects.json'
import searchChecklist from '@/docs/data/checklists/search.json'
import timeChecklist from '@/docs/data/checklists/time.json'
import adminChecklist from '@/docs/data/checklists/admin.json'
import docsChecklist from '@/docs/data/checklists/docs.json'
import feedbackChecklist from '@/docs/data/checklists/feedback.json'
import settingsChecklist from '@/docs/data/checklists/settings.json'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'TestChecklists',
  mixins: [themeClasses],

  data() {
    return {
      selectedChecklist: 'projects',
      showSnackbar: false,
      checklists: {
        docs: docsChecklist,
        feedback: feedbackChecklist,
        pacing: pacingChecklist,
        projects: projectsChecklist,
        search: searchChecklist,
        settings: settingsChecklist,
        time: timeChecklist,
        admin: adminChecklist,
      },
    }
  },

  computed: {
    ...mapAuthGetters(['isUserInternalDev']),
    nearblackTextClass() {
      return this.themeTextClass('nearblack--text', 'white--text')
    },
    coveragePercentTextClass() {
      return this.themeTextClass('grey--text', 'midgray--text')
    },

    availableChecklists() {
      return [
        {
          id: 'projects',
          title: 'Projects View',
          icon: ['fas', 'folder'],
          coverage: this.getChecklistCoverage('projects'),
        },
        {
          id: 'pacing',
          title: 'Pacing View',
          icon: ['fas', 'chart-line'],
          coverage: this.getChecklistCoverage('pacing'),
        },
        {
          id: 'discrepancy',
          title: 'Discrepancy View',
          icon: ['fas', 'balance-scale'],
          coverage: null,
        },
        {
          id: 'time',
          title: 'Time View',
          icon: ['fas', 'clock'],
          coverage: this.getChecklistCoverage('time'),
        },
        {
          id: 'admin',
          title: 'Admin View',
          icon: ['fas', 'shield-alt'],
          coverage: this.getChecklistCoverage('admin'),
        },
        {
          id: 'organization',
          title: 'Organization View',
          icon: ['fas', 'building'],
          coverage: null,
        },
        {
          id: 'person',
          title: 'Person View',
          icon: ['fas', 'user'],
          coverage: null,
        },
        {
          id: 'settings',
          title: 'Settings Module',
          icon: ['fas', 'gear'],
          coverage: this.getChecklistCoverage('settings'),
        },
        {
          id: 'search',
          title: 'Search Bar',
          icon: ['fas', 'magnifying-glass'],
          coverage: this.getChecklistCoverage('search'),
        },
        {
          id: 'docs',
          title: 'Docs Navigation',
          icon: ['fas', 'book'],
          coverage: this.getChecklistCoverage('docs'),
        },
        {
          id: 'feedback',
          title: 'Feedback Flow',
          icon: ['fas', 'message'],
          coverage: this.getChecklistCoverage('feedback'),
        },
      ]
    },

    currentChecklist() {
      return this.checklists[this.selectedChecklist] || {
        title: 'Coming Soon',
        description: 'This checklist is not yet available.',
        sections: [],
      }
    },

    validationSnapshot() {
      const checklistIds = Object.keys(this.checklists)
      return checklistIds.reduce((acc, id) => {
        const summary = this.checklists[id]?.summary
        if (!summary) return acc

        acc.coveredSuites += 1
        acc.total += summary.total || 0
        acc.passed += summary.passed || 0
        acc.failed += summary.failed || 0
        acc.skipped += summary.skipped || 0
        return acc
      }, {
        totalSuites: this.availableChecklists.length,
        coveredSuites: 0,
        total: 0,
        passed: 0,
        failed: 0,
        skipped: 0,
        get coverage() {
          if (!this.total) return 0
          return Math.round((this.passed / this.total) * 100)
        },
      })
    },

    pendingChecklistTitles() {
      return this.availableChecklists
        .filter((checklist) => !this.checklists[checklist.id])
        .map((checklist) => checklist.title)
        .join(', ')
    },
  },

  watch: {
    '$route.params.reportId': {
      immediate: true,
      handler(id) {
        if (id && this.availableChecklists.find((c) => c.id === id)) {
          this.selectedChecklist = id
        }
      },
    },
  },

  methods: {
    getChecklistCoverage(id) {
      const summary = this.checklists[id]?.summary
      if (!summary || !summary.total) return null

      const percentage = (summary.passed / summary.total) * 100
      return Math.round(percentage)
    },

    selectChecklist(id) {
      this.selectedChecklist = id
      this.$router.push({ path: `/docs/test/${id}` }).catch(() => {})
    },

    getCoverageColor(coverage) {
      if (coverage >= 80) return 'success'
      if (coverage >= 60) return 'warning'
      return 'error'
    },

    getSectionColor(section) {
      const { passed, total } = this.getSectionStats(section)
      if (passed === total) return 'success'
      if (passed > 0) return 'warning'
      return 'grey'
    },

    getSectionProgress(section) {
      const { passed, total } = this.getSectionStats(section)
      return `${passed}/${total}`
    },

    getSectionStats(section) {
      const subsections = section.subsections || []
      return subsections.reduce((acc, subsection) => {
        const items = subsection.items || []
        items.forEach((item) => {
          acc.total += 1
          if (item.status === 'passed') acc.passed += 1
        })
        return acc
      }, { passed: 0, total: 0 })
    },

    getItemColor(item) {
      switch (item.status) {
        case 'passed': return 'success'
        case 'failed': return 'error'
        case 'skipped': return 'warning'
        default: return 'grey'
      }
    },

    getItemIcon(item) {
      switch (item.status) {
        case 'passed': return 'mdi-check-circle'
        case 'failed': return 'mdi-close-circle'
        case 'skipped': return 'mdi-minus-circle'
        default: return 'mdi-checkbox-blank-circle-outline'
      }
    },

    getNoteClass(note) {
      if (note.includes('VERIFIED') || note.includes('Fixed')) return 'success--text'
      if (note.includes('BUG')) return 'error--text'
      return 'grey--text'
    },

    getNoteStatusColor(status) {
      if (status === 'Fixed' || status === 'Verified') return 'success'
      if (status === 'Note') return 'info'
      if (status === 'Not Implemented') return 'grey'
      return 'warning'
    },

    getNoteKey(note) {
      return note.issue || note.text || note.description || JSON.stringify(note)
    },

    getNoteIssue(note) {
      return note.issue || 'Note'
    },

    getNoteDescription(note) {
      return note.description || note.text || ''
    },

    async copyToClipboard() {
      const markdown = this.generateMarkdown()
      try {
        await navigator.clipboard.writeText(markdown)
        this.showSnackbar = true
      } catch (err) {
        // Copy failed silently - could not access clipboard
        this.showSnackbar = false
      }
    },

    generateMarkdown() {
      const checklist = this.currentChecklist
      let md = `# ${checklist.title}\n\n`
      md += `${checklist.description}\n\n`
      md += `**Last Tested:** ${checklist.lastTested || 'Not yet tested'}\n\n`
      md += '---\n\n'

      const sections = checklist.sections || []
      sections.forEach((section) => {
        md += `## ${section.title}\n\n`

        const subsections = section.subsections || []
        subsections.forEach((subsection) => {
          md += `### ${subsection.title}\n`

          const items = subsection.items || []
          items.forEach((item) => {
            const checkbox = item.status === 'passed' ? '[x]' : '[ ]'
            const note = item.note ? ` — ${item.note}` : ''
            md += `- ${checkbox} ${item.text}${note}\n`
          })

          md += '\n'
        })
      })

      return md
    },
  },
}
</script>

<style lang="scss" scoped>
.test-checklists-page {
  .sidebar-sticky {
    position: sticky;
    top: 64px;
  }

  .checklist-section {
    border-left: 3px solid #e0e0e0;
    padding-left: 16px;
  }

  .checklist-item {
    line-height: 1.6;
  }
}
</style>
